import React, { useContext, useEffect, useState } from 'react';
import { signInWithPopup } from 'firebase/auth';
import { auth, googleProvider } from '../services/firebase';
import { AuthContext } from '../App';
import { useNavigate } from 'react-router-dom';

const AdminLogin: React.FC = () => {
    const { user, loading } = useContext(AuthContext);
    const navigate = useNavigate();
    const adminEmail = process.env.REACT_APP_ADMIN_EMAIL;
    const [error, setError] = useState('');
    const [signingIn, setSigningIn] = useState(false);

    // If already logged in and is admin, redirect
    useEffect(() => {
        if (!loading && user && user.email === adminEmail) {
            navigate('/admin');
        }
    }, [user, loading, adminEmail, navigate]);

    const handleGoogleSignIn = async () => {
        setSigningIn(true);
        setError('');
        try {
            const result = await signInWithPopup(auth, googleProvider);
            if (result.user.email === adminEmail) {
                navigate('/admin');
            } else {
                await auth.signOut();
                setError('Access denied. Only the site admin can sign in.');
            }
        } catch (err: any) {
            if (err.code === 'auth/popup-closed-by-user') {
                // User closed the popup, no error needed
            } else if (err.code === 'auth/popup-blocked') {
                setError('Popup was blocked. Please allow popups for this site.');
            } else {
                console.error('Sign-in error:', err);
                setError('Sign-in failed. Please try again.');
            }
        } finally {
            setSigningIn(false);
        }
    };

    if (loading) {
        return (
            <div className="mx-auto max-w-3xl md:border-x px-4 py-24">
                <div className="flex items-center justify-center">
                    <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-foreground" />
                </div>
            </div>
        );
    }

    return (
        <div className="mx-auto max-w-3xl md:border-x">
            <div className="px-4 py-24 flex flex-col items-center justify-center min-h-[60vh]">
                <h1 className="text-2xl font-bold text-foreground mb-2">Admin Login</h1>
                <p className="text-sm text-muted-foreground mb-8">
                    Sign in with Google to access the admin panel.
                </p>

                {error && (
                    <p className="text-red-500 text-sm mb-4">{error}</p>
                )}

                <button
                    onClick={handleGoogleSignIn}
                    disabled={signingIn}
                    className="flex items-center gap-3 px-6 py-3 border border-border rounded-md text-sm font-medium text-foreground hover:bg-accent transition-colors duration-200 disabled:opacity-50"
                >
                    {signingIn ? (
                        <>
                            <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-foreground" />
                            Signing in...
                        </>
                    ) : (
                        <>
                            <svg className="w-5 h-5" viewBox="0 0 24 24">
                                <path fill="currentColor" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92a5.06 5.06 0 0 1-2.2 3.32v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.1z" />
                                <path fill="currentColor" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z" />
                                <path fill="currentColor" d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z" />
                                <path fill="currentColor" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z" />
                            </svg>
                            Sign in with Google
                        </>
                    )}
                </button>
            </div>
        </div>
    );
};

export default AdminLogin;
